/**
 * Admin Ministries List Hook (URQL)
 *
 * Query hook for fetching the list of ministries in admin.
 */

import { useQuery } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import type { AdminMinistryListItem } from "../types";

export interface AdminMinistriesFilter {
  isActive?: boolean;
}

interface AdminMinistriesResult {
  ministries: AdminMinistryListItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// QUERY
// =============================================================================

const AdminMinistriesDocument = `
  query AdminMinistries($filter: AdminMinistriesFilter) {
    adminMinistries(filter: $filter) {
      id
      code
      name
      description
      imageUrl
      isActive
      sortOrder
      memberCount
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

export function useAdminMinistries(
  filter?: AdminMinistriesFilter
): AdminMinistriesResult {
  const [result, reexecute] = useQuery<{
    adminMinistries: AdminMinistryListItem[];
  }>({
    query: AdminMinistriesDocument,
    variables: { filter: filter && Object.keys(filter).length ? filter : undefined },
  });

  // Track errors
  if (result.error) {
    errorTracking.addBreadcrumb({
      category: "admin.ministries",
      message: "Failed to load ministries list",
      level: "error",
    });
  }

  return {
    ministries: result.data?.adminMinistries ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}
